import { FC, KeyboardEvent, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { Slider, outlinedInputClasses } from '@mui/material'
import Modal, { TModelProps } from '../../../../shared/modal/Modal'
import ModalTitle from '../../../../shared/modal/ModalTitle'
import ModalContent from '../../../../shared/modal/ModalContent'
import ModalActions from '../../../../shared/modal/ModalActions'
import Button from '../../../../shared/Button'
import Checkbox from '../../../../shared/Checkbox'
import TextField from '../../../../shared/TextField'
import Hint from '../../../../shared/hint/Hint'
import { useFilterContext } from '../../state/useFilter'
import { TCostSettings, useFiltersStateContext } from '../../state/useFiltersState'
import { trackFilter } from '../../utils'

const MAX_PRICE = 10000

const CounterStyled = styled.span`
  color: ${({ theme }) => theme.colors.grey.dark};
  font-size: 18px;
  font-weight: 400;
`

const SectionStyled = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.grey.light};
  border: ${({ theme }) => `1px solid ${theme.colors.grey.primary}`};
  border-radius: 4px;

  & + & {
    margin-top: 12px;
  }
`

const SectionHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

const RangeInputsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  .${outlinedInputClasses.root} {
    background-color: ${({ theme }) => theme.colors.white};
  }

  label {
    color: ${({ theme }) => theme.colors.black};
  }
`

const SliderWrapperStyled = styled.div`
  padding: 0 8px;
`

const ErrorTextStyled = styled.div`
  color: ${({ theme }) => theme.colors.red.primary};
  font-size: 12px;
  line-height: 16px;
  margin-top: 8px;
`

const DEFAULT_COST_SETTINGS: TCostSettings = {
  fixedPriceEnabled: true,
  variablyPricedEnabled: true,
  lowerBound: 0,
  upperBound: MAX_PRICE
}

const parsePrice = (value: string): number | null => {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed || trimmed === 'free') return 0
  const digits = trimmed.replace(/[^0-9.]/g, '')
  const numeric = Number(digits)
  if (!digits || Number.isNaN(numeric)) return null
  return Math.min(Math.max(numeric, 0), MAX_PRICE)
}

const formatPrice = (value: number): string => {
  if (value <= 0) return 'Free'
  if (value >= MAX_PRICE) return '$10,000+'
  return `$${value.toLocaleString('en-US')}`
}

type TCostModalProps = TModelProps

const CostFilterModal: FC<TCostModalProps> = ({ closeModal, open, ...rest }) => {
  const { filterData: { name, subFilters = [] } } = useFilterContext()
  const { setFilterValues, getFilterValues } = useFiltersStateContext()

  const fixedPriceFilter = subFilters.find((filter) => filter.shortName === 'free_fixed_price')
  const variablyPricedFilter = subFilters.find((filter) => filter.shortName === 'variably_priced')
  const allPrices = fixedPriceFilter?.value ?? []

  const [fixedPriceEnabled, setFixedPriceEnabled] = useState(true)
  const [variablyPricedEnabled, setVariablyPricedEnabled] = useState(true)
  const [lowerBound, setLowerBound] = useState(0)
  const [upperBound, setUpperBound] = useState(MAX_PRICE)
  const [lowerInput, setLowerInput] = useState(formatPrice(0))
  const [upperInput, setUpperInput] = useState(formatPrice(MAX_PRICE))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const updateBounds = (lower: number, upper: number) => {
    setLowerBound(lower)
    setUpperBound(upper)
    setLowerInput(formatPrice(lower))
    setUpperInput(formatPrice(upper))
  }

  useLayoutEffect(() => {
    if (!open) return

    setErrorMessage(null)
    const state = getFilterValues('cost') as any
    const settings: TCostSettings = (state?.filters?.length && state.costSettings) || DEFAULT_COST_SETTINGS

    setFixedPriceEnabled(settings.fixedPriceEnabled)
    setVariablyPricedEnabled(settings.variablyPricedEnabled)
    updateBounds(settings.lowerBound, settings.upperBound)
  }, [open])

  const closeModalHandler = () => {
    closeModal?.()
  }

  const toggleFixedPrice = () => {
    if (fixedPriceEnabled && !variablyPricedEnabled) {
      setErrorMessage('At least one box must be checked.')
      return
    }
    setErrorMessage(null)
    setFixedPriceEnabled((current) => !current)
  }

  const toggleVariablyPriced = () => {
    if (variablyPricedEnabled && !fixedPriceEnabled) {
      setErrorMessage('At least one box must be checked.')
      return
    }
    setErrorMessage(null)
    setVariablyPricedEnabled((current) => !current)
  }

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const [lower, upper] = newValue as number[]
    setErrorMessage(null)
    updateBounds(lower, upper)
  }

  const commitLowerBound = () => {
    const parsed = parsePrice(lowerInput)
    if (parsed === null) {
      setLowerInput(formatPrice(lowerBound))
      return
    }
    setErrorMessage(null)
    updateBounds(parsed, Math.max(parsed, upperBound))
  }

  const commitUpperBound = () => {
    const parsed = parsePrice(upperInput)
    if (parsed === null) {
      setUpperInput(formatPrice(upperBound))
      return
    }
    setErrorMessage(null)
    updateBounds(Math.min(parsed, lowerBound), parsed)
  }

  const commitOnEnter = (commit: () => void) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') commit()
  }

  const totalSelected = (fixedPriceEnabled ? 1 : 0) + (variablyPricedEnabled ? 1 : 0)

  const handleApply = () => {
    const selectedPrices = fixedPriceEnabled
      ? allPrices.filter((price) => {
        const numeric = parsePrice(price) ?? 0
        return numeric >= lowerBound && (upperBound >= MAX_PRICE || numeric <= upperBound)
      })
      : []

    if (fixedPriceEnabled && !variablyPricedEnabled && !selectedPrices.length) {
      setErrorMessage('No fixed-price datasets fall within the selected range.')
      return
    }

    const variablyPricedValues = variablyPricedEnabled ? (variablyPricedFilter?.value ?? []) : []
    const combinedValues = [...selectedPrices, ...variablyPricedValues]

    setFilterValues('cost', combinedValues, undefined, undefined, {
      fixedPriceEnabled,
      variablyPricedEnabled,
      lowerBound,
      upperBound
    })
    trackFilter(name, combinedValues)
    closeModalHandler()
  }

  return (
    <Modal {...rest} open={open} closeModal={closeModalHandler} withCloseButton>
      <ModalTitle sx={{ paddingBottom: '16px' }}>
        {name}
        <CounterStyled>
          {' '}
          (
          {totalSelected}
          {' '}
          selected)
        </CounterStyled>
      </ModalTitle>
      <ModalContent sx={{ paddingBottom: '8px', overflowX: 'hidden' }}>
        {fixedPriceFilter && (
          <SectionStyled>
            <SectionHeaderStyled>
              <Checkbox
                checked={fixedPriceEnabled}
                onChange={toggleFixedPrice}
                label={fixedPriceFilter.name}
              />
              <Hint
                placement="top"
                iconWidth={18}
                title="Includes datasets with a set price per release (typical of most datasets contained in Health Data Select)."
              />
            </SectionHeaderStyled>

            <RangeInputsStyled>
              <TextField
                label="Lower bound"
                value={lowerInput}
                onChange={(e) => setLowerInput(e.target.value)}
                onBlur={commitLowerBound}
                onKeyDown={commitOnEnter(commitLowerBound)}
                disabled={!fixedPriceEnabled}
                sx={{ width: '120px' }}
              />
              <TextField
                label="Upper bound"
                value={upperInput}
                onChange={(e) => setUpperInput(e.target.value)}
                onBlur={commitUpperBound}
                onKeyDown={commitOnEnter(commitUpperBound)}
                disabled={!fixedPriceEnabled}
                sx={{ width: '120px' }}
              />
            </RangeInputsStyled>

            <SliderWrapperStyled>
              <Slider
                value={[lowerBound, upperBound]}
                onChange={handleSliderChange}
                min={0}
                max={MAX_PRICE}
                step={100}
                disabled={!fixedPriceEnabled}
                valueLabelDisplay="auto"
                valueLabelFormat={formatPrice}
              />
            </SliderWrapperStyled>
          </SectionStyled>
        )}

        {variablyPricedFilter && (
          <SectionStyled>
            <SectionHeaderStyled>
              <Checkbox
                checked={variablyPricedEnabled}
                onChange={toggleVariablyPriced}
                label={`${variablyPricedFilter.name} (regardless of price)`}
              />
              <Hint
                placement="top"
                iconWidth={18}
                title="Includes datasets with non-standard pricing, such as user-based models and variations based on organization type (non-profit vs for-profit)."
              />
            </SectionHeaderStyled>
          </SectionStyled>
        )}

        {errorMessage && <ErrorTextStyled>{errorMessage}</ErrorTextStyled>}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={closeModalHandler}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleApply}>
          Apply
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default CostFilterModal
